import { NEVER_ENDING_DATE } from 'components/uh-home/SpecialOfferBanner';
import { ShopifyStore } from 'lib/commerce/shopify/shopifyHelper';
import { OffersDataType } from 'components/ring-air/OffersCarousel';

/**
 * Types for country-specific configuration
 */
export type SpecialOffer = {
  id: string;
  title: string | React.ReactNode;
  image?: string[];
  endDate?: string;
  highlighted?: boolean;
};

export type BundleOption = {
  id: string;
  name: string;
  description: string;
  products: string[];
  discountPercentage: number;
};

export type CountryConfig = {
  // Feature flags
  showUhx: boolean;
  showBundles: boolean;
  enableTradeIn: boolean;

  // Product configuration
  featuredAddons: string[];
  defaultAddon: string | null;

  // Offers & discounts
  specialOffers: SpecialOffer[];
  bundleOptions: BundleOption[];

  //secondaryOffers
  secondaryOffers?: OffersDataType[];

  // Shipping & tax
  isFreeShipping: boolean;
  estimatedDeliveryDays: number;
  expressShippingAvailable: boolean;
  expressShippingCost: number;
  taxIncluded: boolean;
  taxRate: number;

  // UI customization
  customHeaderComponent?: string;
  customFooterComponent?: string;
  customProductComponent?: string;
  primaryColorHex?: string;
  secondaryColorHex?: string;

  // Legal & compliance
  requiresAgeVerification: boolean;
  showEuCookieBanner: boolean;
  termsUrl: string;

  // Payment options
  enabledPaymentMethods: string[];
  installmentPaymentAvailable: boolean;
  installmentOptions?: {
    count: number;
    provider: string;
    interestRate?: number;
  }[];

  // Retouch flow (returning visitor discount modal)
  retouchFlow?: {
    ringBuyPage?: boolean;
  };
};

/**
 * Default configuration that applies to all countries
 */
const defaultConfig: CountryConfig = {
  // Feature flags
  showUhx: false,
  showBundles: false,
  enableTradeIn: false,

  // Product configuration
  featuredAddons: ['Year1'],
  defaultAddon: null,

  // Offers & discounts
  specialOffers: [],
  bundleOptions: [],

  // Shipping & tax
  isFreeShipping: false,
  estimatedDeliveryDays: 10,
  expressShippingAvailable: false,
  expressShippingCost: 0,
  taxIncluded: false,
  taxRate: 0,

  // UI customization - no custom components by default

  // Legal & compliance
  requiresAgeVerification: false,
  showEuCookieBanner: false,
  termsUrl: 'https://ultrahuman.com/terms',

  // Payment options
  enabledPaymentMethods: ['credit_card', 'paypal'],
  installmentPaymentAvailable: false,

  // Retouch flow - active by default for all pages
  retouchFlow: {
    ringBuyPage: false,
  },
};

/**
 * Country specific overrides
 */
const countryConfigs: Record<string, Partial<CountryConfig>> = {
  // India specific configuration
  IN: {
    showUhx: true,
    showBundles: true,
    featuredAddons: ['Year1', 'Year2'],
    defaultAddon: 'Year1',
    // secondaryOffers: [
    //   {
    //     title: 'Secondary Offers would be displayed here !',
    //   },
    // ],
    // specialOffers: [
    //   {
    //     id: 'no_cost_emi',
    //     title: 'No cost EMI starting at',
    //     image: ['web_v2/icons/primary_offers/0-percent.png'],
    //     endDate: NEVER_ENDING_DATE,
    //   },
    //   {
    //     id: 'cred_pay',
    //     title: 'CRED Pay | Earn rewards, pay later options',
    //     image: ['web_v2/icons/primary_offers/cred.png'],
    //   },
    //   {
    //     id: 'easy_upi',
    //     title: 'Easy payments with UPI',
    //     image: ['web_v2/icons/primary_offers/upi.png'],
    //   },
    // ],
    isFreeShipping: true,
    estimatedDeliveryDays: 5,
    expressShippingAvailable: true,
    expressShippingCost: 500,
    taxIncluded: true,
    taxRate: 18, // GST
    customHeaderComponent: 'IndiaHeader',
    enabledPaymentMethods: ['credit_card', 'upi', 'netbanking'],
    installmentPaymentAvailable: true,
    installmentOptions: [
      { count: 3, provider: 'bajaj', interestRate: 0 },
      { count: 6, provider: 'bajaj', interestRate: 12 },
    ],
  },

  // United States specific configuration
  US: {
    showUhx: true,
    showBundles: true,
    featuredAddons: ['Year1', 'Year2'],
    specialOffers: [
      {
        id: 'hsa_fsa',
        title: 'HSA / FSA | Powered by Flex',
        image: ['web_v2/icons/primary_offers/hsafsa.png'],
      },
      {
        id: 'apr_financing',
        title: 'As low as 0 APR financing | Klarna, AfterPay & Affirm',
        image: [
          'web_v2/icons/primary_offers/afterpay.png',
          'web_v2/icons/primary_offers/klara.png',
          'web_v2/icons/primary_offers/affirmm.png',
        ],
        endDate: NEVER_ENDING_DATE,
      },
      {
        id: 'shop_pay',
        title: 'Shop Pay | Pay in full or in parts',
        image: ['web_v2/icons/primary_offers/shoppay.png'],
        endDate: NEVER_ENDING_DATE,
      },
      {
        id: 'instant_pay',
        title: 'Instant payment with PayPal, Apple Pay, or GPay',
        image: [
          'web_v2/icons/primary_offers/paypal.png',
          'web_v2/icons/primary_offers/applepay.png',
          'web_v2/icons/primary_offers/gpay.png',
        ],
      },
      {
        id: 'crypto_usdc',
        title: 'Crypto payments with USDC',
        image: ['web_v2/icons/primary_offers/usdc-2.png'],
      },
    ],
    estimatedDeliveryDays: 7,
    expressShippingAvailable: true,
    expressShippingCost: 15,
    taxIncluded: false,
    taxRate: 0, // Varies by state, calculated at checkout
    enabledPaymentMethods: ['credit_card', 'paypal', 'apple_pay', 'google_pay'],
    installmentPaymentAvailable: true,
    installmentOptions: [{ count: 4, provider: 'afterpay', interestRate: 0 }],
    retouchFlow: {
      ringBuyPage: false,
    },
  },

  // Puerto Rico specific configuration (same as US)
  PR: {
    showUhx: true,
    showBundles: true,
    featuredAddons: ['Year1', 'Year2'],
    specialOffers: [
      {
        id: 'hsa_fsa',
        title: 'HSA / FSA | Powered by Flex',
        image: ['web_v2/icons/primary_offers/hsafsa.png'],
      },
      {
        id: 'apr_financing',
        title: 'As low as 0 APR financing | Klarna, AfterPay & Affirm',
        image: [
          'web_v2/icons/primary_offers/afterpay.png',
          'web_v2/icons/primary_offers/klara.png',
          'web_v2/icons/primary_offers/affirmm.png',
        ],
        endDate: NEVER_ENDING_DATE,
      },
      {
        id: 'shop_pay',
        title: 'Shop Pay | Pay in full or in parts',
        image: ['web_v2/icons/primary_offers/shoppay.png'],
        endDate: NEVER_ENDING_DATE,
      },
      {
        id: 'instant_pay',
        title: 'Instant payment with PayPal, Apple Pay, or GPay',
        image: [
          'web_v2/icons/primary_offers/paypal.png',
          'web_v2/icons/primary_offers/applepay.png',
          'web_v2/icons/primary_offers/gpay.png',
        ],
      },
      {
        id: 'crypto_usdc',
        title: 'Crypto payments with USDC',
        image: ['web_v2/icons/primary_offers/usdc-2.png'],
      },
    ],
    estimatedDeliveryDays: 7,
    expressShippingAvailable: true,
    expressShippingCost: 15,
    taxIncluded: false,
    taxRate: 0, // Varies by location, calculated at checkout
    enabledPaymentMethods: ['credit_card', 'paypal', 'apple_pay', 'google_pay'],
    installmentPaymentAvailable: true,
    installmentOptions: [{ count: 4, provider: 'afterpay', interestRate: 0 }],
    retouchFlow: {
      ringBuyPage: false,
    },
  },

  // Canada specific configuration
  CA: {
    showUhx: true,
    showBundles: true,
    featuredAddons: ['Year1', 'Year2'],
    specialOffers: [
      {
        id: 'instant_pay',
        title: 'Instant payment with PayPal or Apple Pay',
        image: [
          'web_v2/icons/primary_offers/paypal.png',
          'web_v2/icons/primary_offers/applepay.png',
        ],
      },
      {
        id: 'crypto_usdc',
        title: 'Crypto payments with USDC',
        image: ['web_v2/icons/primary_offers/usdc-2.png'],
      },
    ],
    estimatedDeliveryDays: 7,
    expressShippingAvailable: true,
    expressShippingCost: 15,
    taxIncluded: false,
    taxRate: 0, // Varies by province, calculated at checkout
    enabledPaymentMethods: ['credit_card', 'paypal', 'apple_pay'],
    installmentPaymentAvailable: false,
  },

  // United Kingdom specific configuration
  GB: {
    showUhx: true,
    featuredAddons: ['Year2'],
    defaultAddon: 'Year2',
    specialOffers: [
      {
        id: 'no_cost_emi',
        title: 'No cost EMI starting at',
        image: ['web_v2/icons/primary_offers/0-percent.png'],
        endDate: NEVER_ENDING_DATE,
      },
      {
        id: 'cred_pay',
        title: 'CRED Pay | Earn rewards, pay later options',
        image: ['web_v2/icons/primary_offers/cred.png'],
      },
      {
        id: 'easy_upi',
        title: 'Easy payments with UPI',
        image: ['web_v2/icons/primary_offers/upi.png'],
      },
    ],
    estimatedDeliveryDays: 5,
    expressShippingAvailable: true,
    expressShippingCost: 10,
    taxIncluded: true,
    taxRate: 20, // VAT
    enabledPaymentMethods: ['credit_card', 'paypal', 'apple_pay'],
    showEuCookieBanner: true,
    retouchFlow: {
      ringBuyPage: false,
    },
  },

  // European Union specific configuration
  EU: {
    showUhx: true,
    featuredAddons: ['Year1', 'Year2'],
    specialOffers: [
      {
        id: 'no_cost_emi',
        title: 'No cost EMI starting at',
        image: ['web_v2/icons/primary_offers/0-percent.png'],
        endDate: NEVER_ENDING_DATE,
      },
      {
        id: 'cred_pay',
        title: 'CRED Pay | Earn rewards, pay later options',
        image: ['web_v2/icons/primary_offers/cred.png'],
      },
      {
        id: 'easy_upi',
        title: 'Easy payments with UPI',
        image: ['web_v2/icons/primary_offers/upi.png'],
      },
    ],
    estimatedDeliveryDays: 7,
    expressShippingAvailable: true,
    expressShippingCost: 12,
    taxIncluded: true,
    taxRate: 21, // Average EU VAT
    customFooterComponent: 'EuFooter',
    enabledPaymentMethods: ['credit_card', 'paypal', 'sepa', 'sofort'],
    showEuCookieBanner: true,
  },

  // UAE specific configuration
  AE: {
    showUhx: false,
    featuredAddons: ['Year2'],
    specialOffers: [
      {
        id: 'no_cost_emi',
        title: 'No cost EMI starting at',
        image: ['web_v2/icons/primary_offers/0-percent.png'],
        endDate: NEVER_ENDING_DATE,
      },
      {
        id: 'cred_pay',
        title: 'CRED Pay | Earn rewards, pay later options',
        image: ['web_v2/icons/primary_offers/cred.png'],
      },
      {
        id: 'easy_upi',
        title: 'Easy payments with UPI',
        image: ['web_v2/icons/primary_offers/upi.png'],
      },
    ],
    estimatedDeliveryDays: 6,
    expressShippingAvailable: true,
    expressShippingCost: 50,
    taxIncluded: true,
    taxRate: 5, // VAT
    enabledPaymentMethods: ['credit_card', 'apple_pay', 'tabby'],
    installmentPaymentAvailable: true,
    installmentOptions: [{ count: 4, provider: 'tabby', interestRate: 0 }],
  },
};

/**
 * Get configuration specific to a country
 */
export const getCountrySpecificConfig = (
  countryCode: string,
): CountryConfig => {
  // Normalize country code
  const normalizedCountryCode = countryCode.toUpperCase();

  // Get country specific overrides or empty object if none exist
  let countryOverrides = countryConfigs[normalizedCountryCode] || {};

  // For EU countries, merge EU configuration
  if (
    normalizedCountryCode !== 'EU' &&
    normalizedCountryCode !== 'GB' &&
    normalizedCountryCode !== 'IN' &&
    normalizedCountryCode !== 'US' &&
    normalizedCountryCode !== 'CA' &&
    normalizedCountryCode !== 'AE'
  ) {
    const shopifyStore = getShopifyStoreForCountry(normalizedCountryCode);
    if (shopifyStore === ShopifyStore.EU) {
      countryOverrides = {
        ...countryConfigs['EU'],
        ...countryOverrides,
      };
    }
  }

  // Merge default config with country specific overrides
  return {
    ...defaultConfig,
    ...countryOverrides,
  };
};

/**
 * Helper to determine ShopifyStore from country code
 */
const getShopifyStoreForCountry = (countryCode: string): ShopifyStore => {
  const EU_COUNTRIES = [
    'AT',
    'BE',
    'BG',
    'HR',
    'CY',
    'CZ',
    'DK',
    'EE',
    'FI',
    'FR',
    'DE',
    'GR',
    'HU',
    'IE',
    'IT',
    'LV',
    'LT',
    'LU',
    'MT',
    'NL',
    'PL',
    'PT',
    'RO',
    'SK',
    'SI',
    'ES',
    'SE',
  ];

  switch (countryCode) {
    case 'IN':
      return ShopifyStore.IN;
    case 'US':
      return ShopifyStore.US;
    case 'GB':
      return ShopifyStore.GB;
    case 'AE':
    case 'SA':
    case 'QA':
    case 'BH':
    case 'KW':
    case 'OM':
      return ShopifyStore.AE;
    default:
      // Check if country is in EU
      if (EU_COUNTRIES.includes(countryCode)) {
        return ShopifyStore.EU;
      }

      return ShopifyStore.ROW;
  }
};
